import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
	Form,
	Message,
	Tab
} from 'semantic-ui-react';

import { log } from '../../../../../../services/utils';

import { useAuthState } from '../../../../../../contexts/AuthContext';

import Actions from '../../../../../Shared/Actions';
import TemplateLayout from './TemplateLayout';
import FormErrors from '../../../../../Shared/FormErrors';
import ReadonlyLayout from './ReadonlyLayout';
import SectionHeader from '../../../../../Shared/SectionHeader';
import HelpModal from '../../../../../Shared/HelpModal';

const LayoutTab = ( {
	errors,
	forceValidation,
	formData,
	formDataHasChanged,
	handleSave,
	resetFormData,
	updateField,
	validate
} ) => {
	const history = useHistory();
	const { id: programId } = useParams();

	const { user: { permissions } } = useAuthState();

	const [ editable, setEditable ] = useState( !formData.id );
	const [ isSaving, setIsSaving ] = useState( false );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const save = useCallback( async () => {
		const cb = () => {
			if ( !validate() ) {
				setEditable( false );
			}
		};

		setIsSaving( true );
		await handleSave( cb );
		setIsSaving( false );
	}, [
		handleSave,
		validate
	] );

	const saveAction = useMemo(
		() => ( {
			action: save,
			disable: !formDataHasChanged || isSaving,
			icon: 'save',
			permission: true,
			text: formData.id === 0
				? 'Save As New Template'
				: 'Save'
		} ),
		[
			formData.id,
			formDataHasChanged,
			isSaving,
			save
		]
	);

	const cancel = useCallback( () => {
		resetFormData();

		if ( formData.id === 0 ) {
			history.push( {
				pathname: `/operations/programs/${ programId }`,
				state: { activeIndex: 3 }
			} );
		} else {
			setEditable( false );
		}
	}, [
		formData.id,
		history,
		programId,
		resetFormData
	] );

	const cancelAction = useMemo(
		() => ( {
			action: cancel,
			icon: 'times',
			permission: true,
			text: 'Cancel'
		} ),
		[ cancel ]
	);

	const edit = useCallback( () => {
		setEditable( true );
	}, [] );

	// this is a hard coded use of the permission addition planned for 1.5
	const editAction = useMemo( () => ( {
		action: edit,
		disable: !permissions.editPrograms.canExecute || ( formData.locked && !permissions.editLockedProgramSubmissionTemplates.canExecute ),
		icon: formData.locked && !permissions.editLockedProgramSubmissionTemplates.canExecute
			? 'lock'
			: 'pencil',
		permission: permissions.editPrograms.isAvailable,
		text: 'Edit'
	} ), [
		edit,
		formData.locked,
		permissions
	] );

	const actions = useMemo(
		() => editable
			? [
				cancelAction,
				saveAction
			]
			: [ editAction ],
		[
			cancelAction,
			editable,
			saveAction,
			editAction
		]
	);

	return (
		<Tab.Pane as="div">
			{ log( 'layoutTab' ) }
			<Actions actions={ actions } />
			<SectionHeader
				title={
					<>
						<span>Template Layout</span>
						<HelpModal id={ 22 } />
					</>
				}
			/>
			<Form
				error={ forceValidation && validate() }
			>
				{ editable
					? <TemplateLayout
						error={ forceValidation && errors.layout[ 0 ].error }
						formData={ formData }
						updateField={ updateField }
					/>
					: <ReadonlyLayout
						formData={ formData }
					/>
				}
				<FormErrors mapFormErrors={ mapFormErrors } />
			</Form>
		</Tab.Pane>
	);
};

LayoutTab.propTypes = {
	errors: PropTypes.object,
	forceValidation: PropTypes.bool,
	formData: PropTypes.object,
	formDataHasChanged: PropTypes.bool,
	handleSave: PropTypes.func,
	resetFormData: PropTypes.func,
	updateField: PropTypes.func,
	validate: PropTypes.func
};

export default memo( LayoutTab );
